import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../utils/apiConfig";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import CategorySidebar from "../../../components/domains/advisor-portal/documents/CategorySidebar";
import DocumentTabs from "../../../components/domains/advisor-portal/documents/DocumentTabs";
import DocumentTable from "../../../components/domains/advisor-portal/documents/DocumentTable";
import UploadModal from "../../../components/domains/advisor-portal/documents/UploadModal";

import logger from '../../../utils/logger';

const DocumentCenter = () => {
  const { householdId } = useParams();
  const navigate = useNavigate();

  // State management
  const [activeTab, setActiveTab] = useState("library");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);

  // Document categories with icons and counts
  const categories = [
    { id: "all", name: "All Documents", count: 23, icon: "📁" },
    { id: "financial", name: "Financial Plans", count: 5, icon: "💰" },
    { id: "tax", name: "Tax Documents", count: 8, icon: "📊" },
    { id: "legal", name: "Legal Documents", count: 3, icon: "⚖️" },
    { id: "insurance", name: "Insurance", count: 4, icon: "🛡️" },
    { id: "investment", name: "Investment", count: 3, icon: "📈" },
  ];

  // Tabs configuration
  const tabs = [
    { id: "library", label: "Document Library", icon: "📚" },
    { id: "recent", label: "Recent Activity", icon: "🕒" },
    { id: "shared", label: "Shared Documents", icon: "🤝" },
    { id: "archived", label: "Archive", icon: "📦" },
  ];

  // Mock documents data
  const mockDocuments = [
    {
      id: 1,
      name: "Q4_2023_Financial_Review.pdf",
      category: "Financial Plans",
      dateModified: "2024-02-15",
      size: "2.4 MB",
      status: "active",
    },
    {
      id: 2,
      name: "2023_Tax_Return.pdf",
      category: "Tax Documents",
      dateModified: "2024-01-30",
      size: "5.1 MB",
      status: "active",
    },
    // Add more mock documents as needed
  ];

  const breadcrumbItems = [
    { label: "Advisor Portal", path: "/advisor-portal" },
    { label: "Client Database", path: "/advisor-portal/client-database" },
    {
      label: "Household Profile",
      path: `/advisor-portal/client-database/household-profile/${householdId}`,
    },
    {
      label: "Document Center",
      path: `/advisor-portal/client-database/household-profile/${householdId}/documents`,
    },
  ];

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/advisor-portal/client-database/household-profile/${householdId}/documents`,
      );
      setDocuments(response.data);
    } catch (error) {
      logger.error("Error fetching documents:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchHouseholdData = async () => {
      try {
        // Include client-database in the URL to match frontend routes
        const url = `/advisor-portal/client-database/household-profile/${householdId}/documents`;
        logger.info("🔍 Attempting to fetch from:", url);

        const response = await api.get(url);

        logger.info("✅ Response received:", response.data);
        setHousehold(response.data);
        setLoading(false);
      } catch (err) {
        logger.error("❌ Error fetching document data:", err);
        logger.info("Error details:", {
          status: err.response?.status,
          data: err.response?.data,
          url: err.config?.url,
          headers: err.config?.headers,
        });
        setError(err.message || "Error fetching document data");
        setLoading(false);
      }
    };

    fetchHouseholdData();
  }, [householdId, navigate]);

  // Document actions
  const handleViewDocument = (doc) => {
    logger.info("Viewing document:", doc);
    // Implement document viewing logic
  };

  const handleDeleteDocument = (doc) => {
    logger.info("Deleting document:", doc);
    // Implement document deletion logic
  };

  const handleDownloadDocument = (doc) => {
    logger.info("Downloading document:", doc);
    // Implement document download logic
  };

  const handleShareDocument = (doc) => {
    logger.info("Sharing document:", doc);
    // Implement document sharing logic
  };

  const handleUpload = async (file) => {
    logger.info("Uploading file:", file);
    // Implement actual file upload logic here
    return new Promise((resolve) => setTimeout(resolve, 2000));
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  if (loading) {
    return (
      <SubPageLayout title="Loading..." breadcrumbItems={breadcrumbItems}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </SubPageLayout>
    );
  }

  if (error) {
    return (
      <SubPageLayout title="Error" breadcrumbItems={breadcrumbItems}>
        <div className="text-red-500 text-center">{error}</div>
      </SubPageLayout>
    );
  }

  return (
    <SubPageLayout
      title="Document Center"
      subtitle={`Manage documents for ${household?.name || "Household"}`}
      breadcrumbItems={breadcrumbItems}
    >
      <div className="max-w-7xl mx-auto">
        {/* Top Action Bar */}
        <div className="bg-gray-50 dark:bg-gray-800/50 shadow rounded-lg mb-6 p-4">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            {/* Search Bar */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search documents..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  value={searchQuery}
                  onChange={handleSearch}
                />
                <div className="absolute left-3 top-2.5">
                  <svg
                    className="h-5 w-5 text-gray-400 dark:text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Upload Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg
                className="-ml-1 mr-2 h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Upload Documents
            </button>
          </div>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Sidebar - Categories */}
          <div className="col-span-12 lg:col-span-3">
            <CategorySidebar
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
            />
          </div>

          {/* Main Content Area */}
          <div className="col-span-12 lg:col-span-9">
            <div className="bg-gray-50 dark:bg-gray-800/50 shadow rounded-lg">
              <DocumentTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
              <DocumentTable
                documents={documents.length > 0 ? documents : mockDocuments}
                onView={handleViewDocument}
                onDelete={handleDeleteDocument}
                onDownload={handleDownloadDocument}
                onShare={handleShareDocument}
                className="bg-gray-50 dark:bg-gray-800/50"
              />
            </div>
          </div>
        </div>
      </div>

      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUpload}
      />
    </SubPageLayout>
  );
};

export default DocumentCenter;
